import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { locations } from '../data/locations';
import Hero from '../components/sections/Hero';
import AuditForm from '../components/sections/AuditForm';
import CaseStudySection from '../components/sections/CaseStudySection';
import ServicePackages from '../components/sections/ServicePackages';
import AEOSOP from '../components/sections/AEOSOP';
import VisibilityScore from '../components/sections/VisibilityScore';
import SEO from '../components/common/SEO';
import SafeIcon from '../components/common/SafeIcon';
import { FiCheck, FiInfo, FiExternalLink, FiMapPin, FiAward, FiFileText } from 'react-icons/fi';

const CityPage = () => {
  const { citySlug } = useParams();
  const cityData = locations.find(l => l.slug === citySlug);

  if (!cityData) return <Navigate to="/" replace />;

  const cityDefinition = `${cityData.city} SEO is the process of optimizing a business entity for the ${cityData.city} Map Pack using the Truth Protocol™. It involves aligning JSON-LD schema with municipal authorities and structuring content for Retrieval-Augmented Generation (RAG) to ensure AI assistants like ChatGPT recommend your services.`;

  return (
    <>
      <SEO 
        title={`${cityData.city} SEO Experts | AlbertaRank™ AEO`} 
        description={cityData.description}
      />
      
      <Hero 
        title={`${cityData.city} Local Dominance`} 
        subtitle={`AlbertaRank increases revenue for ${cityData.city} contractors through the Truth Protocol™ and precision Answer Engine Optimization.`} 
        city={cityData.city}
        definition={cityDefinition}
      />

      {/* Answer-First Section (Step 3 of SOP) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-black uppercase mb-6">
                <SafeIcon icon={FiInfo} /> AEO Direct Answer Block
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                {cityData.aeoPrompt}
              </h2>
              <div className="p-8 bg-slate-50 border-l-8 border-orange-500 rounded-2xl mb-10">
                <p className="text-xl md:text-2xl text-slate-800 italic font-medium leading-relaxed">
                  "{cityData.aeoAnswer}"
                </p>
                <p className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest">— Truth Protocol™ Output</p>
              </div>

              {/* Entity Identification Data (Step 1 of SOP) */}
              <div className="grid sm:grid-cols-2 gap-6 pt-8 border-t border-slate-100">
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 flex items-center gap-2">
                    <SafeIcon icon={FiMapPin} className="text-orange-500" /> Entity Address
                  </h4>
                  <p className="text-sm text-slate-600">{cityData.entityData?.address}</p>
                </div>
                <div className="space-y-4">
                  <h4 className="font-bold text-slate-900 flex items-center gap-2">
                    <SafeIcon icon={FiAward} className="text-orange-500" /> Authorized Licenses
                  </h4>
                  <p className="text-sm text-slate-600">{cityData.entityData?.license}</p>
                </div>
              </div>
            </div>

            <div className="sticky top-32">
              <VisibilityScore />
            </div>
          </div>
        </div>
      </section>

      <AEOSOP city={cityData.city} />

      <ServicePackages city={cityData.city} packages={cityData.packages} />

      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-orange-600 font-bold uppercase tracking-wider text-sm mb-4">The Citation Loop</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Proven Authority in {cityData.city}</h3>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We don't just build links; we build <strong>Entity Authority</strong>. By connecting your business to the <a href={cityData.externalAuthority} target="_blank" rel="noopener noreferrer" className="text-orange-600 underline font-semibold inline-flex items-center gap-1">official {cityData.city} business ecosystem <SafeIcon icon={FiExternalLink} /></a>, we prove your relevance.
              </p>
              
              <div className="bg-slate-900 p-8 rounded-3xl text-white">
                <h4 className="text-xl font-bold mb-4">Certs & Verification</h4>
                <div className="flex flex-wrap gap-3">
                  {cityData.entityData?.certs.map((cert, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white/10 rounded-lg text-xs font-medium border border-white/10">
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-slate-50 p-1 rounded-[2.5rem]">
              <AuditForm city={cityData.city} title={`Get Your ${cityData.city} Audit`} />
            </div>
          </div>
        </div>
      </section>

      <CaseStudySection city={cityData.city} />
    </>
  );
};

export default CityPage;